import { useEffect, useRef, useState } from "react";
import { keycloakService } from "../services/keycloakService";

interface SSENotification {
  id: string;
  title: string;
  message: string;
  notification_type: string;
  created_at: string;
  is_read: boolean;
}

interface SSEEvent {
  type: string;
  notification?: SSENotification;
  message?: string;
}

interface UseSSENotificationsReturn {
  isConnected: boolean;
  error: string | null;
  lastNotification: SSENotification | null;
}

/**
 * Custom hook for managing SSE (Server-Sent Events) notification connection
 * Automatically reconnects with exponential backoff on disconnect
 */
export const useSSENotifications = (): UseSSENotificationsReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastNotification, setLastNotification] =
    useState<SSENotification | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectDelay = 30000; // 30 seconds max
  const baseReconnectDelay = 1000; // 1 second base

  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      const authenticated = keycloakService.isAuthenticated();
      setIsAuthenticated(authenticated);
    };

    checkAuth();
    // Re-check periodically
    const interval = setInterval(checkAuth, 5000);
    return () => clearInterval(interval);
  }, []);

  const calculateReconnectDelay = (): number => {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, reconnectAttemptsRef.current),
      maxReconnectDelay,
    );
    return delay;
  };

  const connect = async () => {
    try {
      // Refresh token before connecting to ensure it's valid
      console.log("🔄 SSE: Refreshing token before connection...");
      await keycloakService.updateToken(30);

      const token = keycloakService.getToken();

      if (!token) {
        console.log("❌ SSE: No authentication token available");
        return;
      }

      // Close existing connection if any
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      // Create new SSE connection with token in URL (EventSource doesn't support headers)
      const apiUrl = import.meta.env.VITE_API_URL;
      const url = `${apiUrl}/v1/sse/notifications?token=${encodeURIComponent(token)}`;

      console.log("📡 SSE: Connecting to notification stream...");
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log("✅ SSE: Connection established");
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0; // Reset reconnect counter on successful connection
      };

      eventSource.onmessage = (event) => {
        try {
          const data: SSEEvent = JSON.parse(event.data);
          console.log("📩 SSE: Received event:", data.type);

          if (data.type === "notification" && data.notification) {
            console.log("🔔 SSE: New notification:", data.notification.title);
            setLastNotification(data.notification);
          } else if (data.type === "connected") {
            console.log("✅ SSE: Connection confirmed by server");
          }
        } catch (err) {
          console.error("❌ SSE: Failed to parse event data:", err);
        }
      };

      eventSource.onerror = (err) => {
        console.error("❌ SSE: Connection error:", err);
        setIsConnected(false);
        setError("Connection lost. Attempting to reconnect...");
        eventSource.close();

        // Schedule reconnection with exponential backoff
        const delay = calculateReconnectDelay();
        console.log(
          `🔄 SSE: Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current + 1})...`,
        );

        reconnectTimeoutRef.current = setTimeout(async () => {
          reconnectAttemptsRef.current++;
          await connect();
        }, delay);
      };
    } catch (err) {
      console.error("❌ SSE: Failed to create connection:", err);
      setError("Failed to establish connection");
      setIsConnected(false);

      // Try again after delay
      const delay = calculateReconnectDelay();
      reconnectTimeoutRef.current = setTimeout(async () => {
        reconnectAttemptsRef.current++;
        await connect();
      }, delay);
    }
  };

  useEffect(() => {
    let tokenRefreshInterval: NodeJS.Timeout | null = null;

    if (isAuthenticated) {
      console.log("🚀 SSE: User authenticated, connecting...");
      connect();

      // Refresh token and reconnect every 4 minutes to prevent token expiration
      // (Tokens usually expire in 5 minutes)
      tokenRefreshInterval = setInterval(
        async () => {
          console.log(
            "🔄 SSE: Refreshing token and reconnecting to prevent expiration...",
          );
          try {
            await keycloakService.updateToken(30);
            // Reconnect with fresh token
            if (eventSourceRef.current) {
              eventSourceRef.current.close();
            }
            await connect();
          } catch (error) {
            console.error("❌ SSE: Token refresh failed:", error);
          }
        },
        4 * 60 * 1000,
      ); // 4 minutes
    } else {
      console.log("⏸️ SSE: User not authenticated, skipping connection");
    }

    // Cleanup on unmount or auth change
    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
      if (eventSourceRef.current) {
        console.log("🔌 SSE: Closing connection");
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated]);

  return {
    isConnected,
    error,
    lastNotification,
  };
};
